import { environment } from '../../../environments/environment';
import {
    Action,
    ActionReducer,
    combineReducers as combine,
    select,
    Store,
    StoreModule,
} from '@ngrx/store';
import { ListItem } from '../models/common/list-item';
import { isArray } from './object';
import { OperatorFunction } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Injector } from '@angular/core';
import { ListSortBy } from '@core/models/common/list-sort-by';
import { ListSortDirection } from '@core/models/common/list-sort-direction';
import { isString } from './string';
import { selectEntityState } from '@core/state/shared/entities/entities-selectors';
import { IEntitiesState } from '@core/state/shared/entities/entities-state';
import { AppGlobal } from './app';

export function isMatchingByFilters(
    filters: any,
    isMatching: boolean,
    item: any
): boolean {
    Object.keys(filters).forEach((filterKey) => {
        if (isMatching) {
            isMatching =
                item?.isMatchingFilter &&
                item.isMatchingFilter(filterKey, filters[filterKey]);
        }
    });

    return isMatching;
}

export function createNamedWrapperReducer(
    reducerFunction: any,
    reducerName: string | string[]
) {
    return (state, action, ...args) => {
        const { name } = action;
        const isInitializationCall = state === undefined;

        if (!isInitializationCall) {
            if (isArray(reducerName)) {
                const reducerNameArray: string[] = Object.assign(
                    [],
                    reducerName
                );
                if (
                    reducerNameArray.findIndex((item) => item === name) === -1
                ) {
                    return state;
                }
            } else if (name !== reducerName) {
                return state;
            }
        }

        return reducerFunction.apply(this, [state, action, ...args]);
    };
}

/**
 *  * Determine if a navigation state contains the comparer root, used to avoid issues with hyphenated navigation route segments
 *
 * @export
 * @param {string} navigationStateRoot
 * @param {string} navState
 * @returns
 */
export function isNavigationStateValidRoot(
    navigationStateRoot: string,
    navState: string
) {
    let result: boolean = true;

    if (
        navigationStateRoot &&
        navigationStateRoot.length &&
        navState &&
        navState.length
    ) {
        const rootSegments: string[] = navigationStateRoot.split('.');
        const navStateSegments: string[] = navState.split('.');

        result = rootSegments.every(
            (segment, index) => segment === navStateSegments[index]
        );
    } else {
        result = false;
    }

    return result;
}

/**
 * Generate a sort function that supports multiple sort keys with : deliminator.
 * Sorts a list of ids referencing the entities objects for values.
 * @param sortBy
 * @param entities
 * @returns {(a:any, b:any)=>ListItem|number}
 */
export function sortListItemIdsFn(
    sortBy: ListSortBy,
    sortDirection: ListSortDirection | ListSortDirection[],
    entities: any
): any {
    const sortByKeys: string[] = sortBy.split(':');

    return (a: any, b: any): number => {
        let aItem: ListItem = entities[a] as ListItem;
        let bItem: ListItem = entities[b] as ListItem;

        // Support multiple key sorting
        let sortDelta: number = 0;
        sortByKeys.forEach((sortByKey, index) => {
            if (!sortDelta) {
                let aItemValue: any = aItem.valueOf(sortByKey);
                let bItemValue: any = bItem.valueOf(sortByKey);
                let filteredSortDirection = isArray(sortDirection)
                    ? sortDirection[index]
                    : sortDirection;

                if (filteredSortDirection === ListSortDirection.ASCENDING) {
                    if (isString(aItemValue)) {
                        sortDelta = aItemValue.localeCompare(bItemValue);
                    } else {
                        sortDelta = aItemValue - bItemValue;
                    }
                } else {
                    if (isString(aItemValue)) {
                        sortDelta = bItemValue.localeCompare(aItemValue);
                    } else {
                        sortDelta = bItemValue - aItemValue;
                    }
                }
            }
        });

        return bItem && aItem && sortDelta;
    };
}

/**
 * Reduce multiple reducers into a single reducer from left to right.
 * @param reducers
 * @returns {(previous:any, current:any)=>any}
 */
// export function reduceReducers(...reducers) {
//     return (previous, current) =>
//         reducers.reduce(
//             (p, r) => r(p, current),
//             previous
//         );
// }
//
// export function reduceReducers(initialState, ...reducers) {
//     return (state, action) => {
//         let isInitial = !state;
//         return reducers.reduce((newState, reducer) => {
//             return reducer(newState, action);
//         }, isInitial ? initialState : { ...state });
//     };
// }

function isObject(value) {
    return (
        typeof value !== 'undefined' &&
        value !== null &&
        value.constructor === Object
    );
}

export function reduceReducers(...reducers) {
    const empty = reducers.length === 0;

    function applyNextState(previousState, nextState) {
        if (isObject(previousState) && isObject(nextState)) {
            return Object.assign(previousState, nextState);
        } else {
            return nextState;
        }
    }

    function checkHasChanged(previousState, nextState) {
        if (isObject(previousState) && isObject(nextState)) {
            if (previousState === nextState) {
                return false;
            } else {
                const keys = Object.keys(nextState);
                for (let key of keys) {
                    if (previousState[key] !== nextState[key]) {
                        return true;
                    }
                }
                return false;
            }
        } else {
            return previousState !== nextState;
        }
    }

    return (state, action, ...args) => {
        if (empty) {
            throw Error('There are no reducers');
        }
        let finalNextState = isObject(state) ? Object.assign({}, state) : state;
        let hasChanged = false;

        function getPreviousState() {
            return typeof state === 'undefined' ? state : finalNextState;
        }

        for (let reducer of reducers) {
            // const nextState = reducers[i](getPreviousState(), action);
            const nextState = reducer.apply(this, [
                getPreviousState(),
                action,
                ...args,
            ]);
            if (checkHasChanged(finalNextState, nextState)) {
                hasChanged = true;
                finalNextState = applyNextState(finalNextState, nextState);
            }
        }

        return hasChanged ? finalNextState : state;
    };
}

/**
 * Returns a reducer function with state calculation determined by the result
 * of invoking the handler key corresponding with the dispatched action type,
 * passing both the current state and action object. Defines default
 * serialization (persistence) handlers based on the presence of a schema.
 *
 * @param  {*}        initialState   Initial state
 * @param  {Object}   customHandlers Object mapping action types to state
 *                                   action handlers
 * @param  {?Object}  schema         JSON schema object for deserialization
 *                                   validation
 * @return {Function}                Reducer function
 */
export function createReducer(
    initialState = null,
    customHandlers = {},
    schema = null
) {
    // Define default handlers for serialization actions. If no schema is
    // provided, always return the initial state. Otherwise, allow for
    // serialization and validate on deserialize.
    let defaultHandlers;
    // if ( schema ) {
    //     defaultHandlers = {
    //         [ SERIALIZE ]: ( state ) => state,
    //         [ DESERIALIZE ]: ( state ) => {
    //             if ( isValidStateWithSchema( state, schema ) ) {
    //                 return state;
    //             }
    //
    //             warn( 'state validation failed - check schema used for:', customHandlers );
    //
    //             return initialState;
    //         }
    //     };
    // } else {
    //     defaultHandlers = {
    //         [ SERIALIZE ]: () => initialState,
    //         [ DESERIALIZE ]: () => initialState
    //     };
    // }

    const handlers = {
        ...defaultHandlers,
        ...customHandlers,
    };

    // When custom serialization behavior is provided, we assume that it may
    // involve heavy logic (mapping, converting from Immutable instance), so
    // we cache the result and only regenerate when state has changed.
    // if ( customHandlers[ SERIALIZE ] ) {
    //     let lastState, lastSerialized;
    //     handlers[ SERIALIZE ] = ( state, action ) => {
    //         if ( state === lastState ) {
    //             return lastSerialized;
    //         }
    //
    //         const serialized = customHandlers[ SERIALIZE ]( state, action );
    //         lastState = state;
    //         lastSerialized = serialized;
    //         return serialized;
    //     };
    // }

    const reducer = (state = initialState, action, ...args) => {
        const { type } = action;

        if (!environment.production && 'type' in action && !type) {
            throw new TypeError(
                'Reducer called with undefined type.' +
                    ' Verify that the action type is defined in state/action-types.js'
            );
        }

        if (handlers.hasOwnProperty(type)) {
            return handlers[type].apply(this, [state, action, ...args]);
        }

        return state;
    };

    // used to propagate actions properly when combined in combineReducersWithPersistence
    // reducer.hasCustomPersistence = true;

    return reducer;
}

/**
 * Creates a schema-validating reducer
 *
 * Use this to wrap simple reducers with a schema-based
 * validation check when loading the initial state from
 * persistent storage.
 *
 * When this wraps a reducer with a known JSON schema,
 * it will intercept the DESERIALIZE action (on app boot)
 * and check if the persisted state is still valid state.
 * If so it will return the persisted state, otherwise
 * it will return the initial state computed from
 * passing the null action.
 *
 * @example
 * const ageReducer = ( state = 0, action ) =>
 *     GROW === action.type
 *         ? state + 1
 *         : state
 *
 * const schema = { type: 'number', minimum: 0 }
 *
 * export const age = withSchemaValidation( schema, age )
 *
 * ageReducer( -5, { type: DESERIALIZE } ) === -5
 * age( -5, { type: DESERIALIZE } ) === 0
 * age( 23, { type: DESERIALIZE } ) === 23
 *
 * If no schema is provided, the reducer will return initial state on SERIALIZE
 * and DESERIALIZE
 *
 * @example
 * const schema = { type: 'number', minimum: 0 }
 * export const age = withSchemaValidation( null, age )
 *
 * ageReducer( -5, { type: SERIALIZE } ) === -5
 * age( -5, { type: SERIALIZE } ) === 0
 * age( 23, { type: SERIALIZE } ) === 0
 * age( 23, { type: DESERIALIZE } ) === 0
 *
 * @param {object} schema JSON-schema description of state
 * @param {function} reducer normal reducer from ( state, action ) to new state
 * @returns {function} wrapped reducer handling validation on DESERIALIZE and
 * returns initial state if no schema is provided on SERIALIZE and DESERIALIZE.
 */
export const withSchemaValidation = (schema, reducer) => {
    const wrappedReducer: any = (state, action) => {
        // if ( SERIALIZE === action.type ) {
        //     return schema ? reducer( state, action ) : reducer( undefined, { type: '@@calypso/INIT' } );
        // }
        // if ( DESERIALIZE === action.type ) {
        //     if ( ! schema ) {
        //         return reducer( undefined, { type: '@@calypso/INIT' } );
        //     }
        //
        //     return state && isValidStateWithSchema( state, schema )
        //         ? state
        //         : reducer( undefined, { type: '@@calypso/INIT' } );
        // }

        return reducer(state, action);
    };

    // used to propagate actions properly when combined in combineReducersWithPersistence
    wrappedReducer.hasCustomPersistence = true;

    return wrappedReducer;
};

/**
 * Returns a single reducing function that ensures that persistence is opt-in.
 * If you don't need state to be stored, simply use this method instead of
 * combineReducers from redux. This function uses the same interface.
 *
 * To mark that a reducer's state should be persisted, add the related JSON
 * schema as a property on the reducer.
 *
 * @example
 * const age = ( state = 0, action ) =>
 *     GROW === action.type
 *         ? state + 1
 *         : state
 * const height = ( state = 150, action ) =>
 *     GROW === action.type
 *         ? state + 1
 *         : state
 * const schema = { type: 'number', minimum: 0 };
 *
 * age.schema = schema;
 *
 * const combinedReducer = combineReducers( {
 *     age,
 *     height
 * } );
 *
 * combinedReducer( { age: -5, height: -5 } ), { type: DESERIALIZE } ); // { age: 0, height: 150 };
 * combinedReducer( { age: -5, height: 123 } ), { type: DESERIALIZE } ); // { age: 0, height: 150 };
 * combinedReducer( { age:  6, height: 123 } ), { type: DESERIALIZE } ); // { age: 6, height: 150 };
 * combinedReducer( { age:  6, height: 123 } ), { type: SERIALIZE } ); // { age: 6, height: 150 };
 * combinedReducer( { age:  6, height: 123 } ), { type: GROW } ); // { age: 7, height: 124 };
 *
 * If the reducer explicitly handles the SERIALIZE and DESERIALZE actions, set
 * the hasCustomPersistence property to true on the reducer.
 *
 * @example
 * const date = ( state = new Date( 0 ), action ) => {
 * 	switch ( action.type ) {
 * 		case 'GROW':
 * 			return new Date( state.getTime() + 1 );
 * 		case SERIALIZE:
 * 			return state.getTime();
 * 		case DESERIALIZE:
 * 			if ( isValidStateWithSchema( state, schema ) ) {
 * 				return new Date( state );
 * 			}
 * 			return new Date( 0 );
 * 		default:
 * 			return state;
 * 	}
 * };
 * date.hasCustomPersistence = true;
 *
 * const combinedReducer = combineReducers( {
 *     date,
 *     height
 * } );
 *
 * combinedReducer( { date: -5, height: -5 } ), { type: DESERIALIZE } ); // { date: new Date( 0 ), height: 150 };
 * combinedReducer( { date: -5, height: 123 } ), { type: DESERIALIZE } ); // { date: new Date( 0 ), height: 150 };
 * combinedReducer( { date:  6, height: 123 } ), { type: DESERIALIZE } ); // { date: new Date( 6 ), height: 150 };
 * combinedReducer( { date: new Date( 6 ), height: 123 } ), { type: SERIALIZE } ); // { date: 6, height: 150 };
 * combinedReducer( { date: new Date( 6 ), height: 123 } ), { type: GROW } ); // { date: new Date( 7 ), height: 124 };
 *
 * @param {object} reducers - object containing the reducers to merge
 * @returns {function} - Returns the combined reducer function
 */
// export function combineReducers(reducers) {
//     const validatedReducers = reduce(reducers, (validated, next: any, key) => {
//         const { schema, hasCustomPersistence } = next;
//         return { ...validated, [ key ]: hasCustomPersistence ? next : withSchemaValidation(schema, next) };
//     }, {});
//     const combined: any = combine(validatedReducers);
//     combined.hasCustomPersistence = true;
//     return combined;
// }

export function combineReducers(
    reducers: any,
    initialState: any = {}
): ActionReducer<any, Action> {
    const reducerKeys = Object.keys(reducers);
    const finalReducers: any = {};

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < reducerKeys.length; i++) {
        const key = reducerKeys[i];
        if (typeof reducers[key] === 'function') {
            finalReducers[key] = reducers[key];
        }
    }

    const finalReducerKeys = Object.keys(finalReducers);

    return function combination(state, action) {
        state = state === undefined ? initialState : state;
        let hasChanged = false;
        const nextState: any = {};
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < finalReducerKeys.length; i++) {
            const key = finalReducerKeys[i];
            const reducer: any = finalReducers[key];
            const previousStateForKey = state[key];
            const nextStateForKey = reducer(
                previousStateForKey,
                action,
                state.entity
            );

            nextState[key] = nextStateForKey;
            hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
        }
        return hasChanged ? nextState : state;
    };
}

/**
 * Checks if the action is a part of the reducer by verifying if the action name falls under the `group`
 * Used by default reducer state, to ensure if they need to process shared actions.
 *
 * @param action
 * @param {string[]} group
 * @returns {number}
 */
export function isActionRelated(action: any, group: string | string[]) {
    let isRelated: boolean = false;

    if (isString(group)) {
        isRelated = action.name === group;
    } else {
        isRelated = group.indexOf(action.name) > -1;
    }

    return isRelated;
}

export function ofName<T extends Action>(
    ...allowedNames: string[]
): OperatorFunction<Action, T> {
    return filter(
        (action: T) => action && allowedNames.indexOf((action as any).name) > -1
    );
}

export function entityMetaReducer(reducer) {
    const w: any = window;
    return (state, action, ...args) => {
        // TODO: Not pretty .. need to hack this a different way to support entity slice into the reducers
        let entitySlice: IEntitiesState;
        const injector = AppGlobal.injector();


        if (!!injector) {
            const store$ = (injector as Injector).get(Store);
            store$
                .pipe(select(selectEntityState), take(1))
                .subscribe((entityState) => {
                    entitySlice = entityState;
                });
        }

        return reducer(state, action, entitySlice);
    };
}
